import React, { Component, ReactNode } from 'react';
import { useNavigate, NavigateFunction } from 'react-router-dom';

interface ChunkLoadErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, retry: () => void, goBack: () => void) => ReactNode;
}

interface ChunkLoadErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  isChunkLoadError: boolean;
}

/**
 * Error boundary specifically designed to handle chunk loading failures in lazy-loaded routes.
 *
 * In an offline-first PWA, when navigating to a lazy-loaded route while offline,
 * the chunk may fail to load if it's not cached. This boundary catches those errors
 * and provides:
 * 1. User feedback about what went wrong
 * 2. Retry mechanism to attempt loading again
 * 3. Navigation fallback to go back to previous page
 *
 * This prevents users from being stuck on a blank screen with no way to navigate.
 */
class ChunkLoadErrorBoundaryClass extends Component<
  ChunkLoadErrorBoundaryProps & { navigate: NavigateFunction },
  ChunkLoadErrorBoundaryState
> {
  constructor(props: ChunkLoadErrorBoundaryProps & { navigate: NavigateFunction }) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkLoadError: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ChunkLoadErrorBoundaryState> {
    // Detect chunk load errors
    const isChunkLoadError =
      error.name === 'ChunkLoadError' ||
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed') ||
      error.message.includes('error loading dynamically imported module');

    return {
      hasError: true,
      error,
      isChunkLoadError,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ChunkLoadErrorBoundary caught error:', {
      error,
      errorInfo,
      isOnline: navigator.onLine,
      timestamp: new Date().toISOString(),
    });

    this.setState({ errorInfo });
  }

  retry = () => {
    // Clear error state and force a re-render
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkLoadError: false,
    });

    // Force a full reload if offline to attempt to use cached chunks
    if (!navigator.onLine) {
      window.location.reload();
    }
  };

  goBack = () => {
    // Clear error state first
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkLoadError: false,
    });

    // Navigate back
    this.props.navigate(-1);
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.retry, this.goBack);
      }

      // Default fallback UI
      return (
        <div className="flex h-screen items-center justify-center bg-background p-4">
          <div className="max-w-md w-full space-y-4 text-center">
            {/* Icon */}
            <div className="flex justify-center">
              <div className="rounded-full bg-destructive/10 p-3">
                <svg
                  className="h-8 w-8 text-destructive"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>

            {/* Error message */}
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold text-foreground">
                {this.state.isChunkLoadError ? 'Page Failed to Load' : 'Something Went Wrong'}
              </h1>

              {this.state.isChunkLoadError ? (
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    {navigator.onLine
                      ? 'The page component failed to load. This might be a temporary network issue.'
                      : 'You are offline and this page is not available yet. Connect to the internet to load it, or go back to continue using the app.'
                    }
                  </p>
                  {!navigator.onLine && (
                    <p className="text-xs bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 p-2 rounded">
                      <strong>Offline Mode:</strong> Some pages need to be loaded at least once while online before they work offline.
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {this.state.error.message}
                </p>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-2 pt-4">
              <button
                onClick={this.goBack}
                className="flex-1 px-4 py-2 text-sm font-medium text-foreground bg-secondary hover:bg-secondary/80 rounded-md transition-colors"
              >
                ← Go Back
              </button>
              <button
                onClick={this.retry}
                className="flex-1 px-4 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 rounded-md transition-colors"
              >
                {navigator.onLine ? 'Try Again' : 'Reload App'}
              </button>
            </div>

            {/* Debug info - only in dev mode or if explicitly enabled */}
            {(import.meta.env.DEV || window.location.search.includes('debug=true')) && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                  Show Error Details
                </summary>
                <div className="mt-2 p-3 bg-secondary/50 rounded text-xs font-mono overflow-auto max-h-40">
                  <div className="space-y-1">
                    <div><strong>Error:</strong> {this.state.error.name}</div>
                    <div><strong>Message:</strong> {this.state.error.message}</div>
                    <div><strong>Online:</strong> {String(navigator.onLine)}</div>
                    <div><strong>Stack:</strong></div>
                    <pre className="whitespace-pre-wrap text-xs opacity-75">
                      {this.state.error.stack}
                    </pre>
                  </div>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Wrapper component to provide router navigate function to class component
 */
export function ChunkLoadErrorBoundary({ children, fallback }: ChunkLoadErrorBoundaryProps) {
  const navigate = useNavigate();

  return (
    <ChunkLoadErrorBoundaryClass navigate={navigate} fallback={fallback}>
      {children}
    </ChunkLoadErrorBoundaryClass>
  );
}
